import { useCallback } from "react";
import {
  useGetXeroStatus,
  usePushInvoiceToXero,
  usePushEstimateToXero,
} from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";

type XeroKind = "invoice" | "estimate";

function extractError(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e?.response?.data?.error || e?.message || "Unknown error";
}

export interface BulkPushSummary {
  total: number;
  succeeded: number;
  failed: number;
  failures: Array<{ id: number; error: string }>;
  cancelled: boolean;
  processed: number;
}

export interface BulkPushProgress {
  current: number;
  total: number;
  succeeded: number;
  failed: number;
}

export interface PushManyOptions {
  onProgress?: (progress: BulkPushProgress) => void;
  signal?: AbortSignal;
}

export function useXeroPush(kind: XeroKind) {
  const { toast } = useToast();
  const { data: status } = useGetXeroStatus();
  const pushInvoice = usePushInvoiceToXero();
  const pushEstimate = usePushEstimateToXero();

  const connected = status?.connected === true;
  const configured = (status as { configured?: boolean } | undefined)?.configured === true;
  const needsReconnect =
    (status as { needsReconnect?: boolean } | undefined)?.needsReconnect === true;
  const available = connected && !needsReconnect;

  const docLabel = kind === "invoice" ? "invoice" : "estimate";

  const guard = useCallback((): boolean => {
    if (!connected) {
      toast({
        title: "Xero not connected",
        description: configured
          ? "Connect Xero in Integrations to push from here."
          : "Xero integration is not configured on this server.",
        variant: "destructive",
      });
      return false;
    }
    if (needsReconnect) {
      toast({
        title: "Xero needs to be reconnected",
        description: "Reconnect Xero in Integrations to push.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  }, [connected, configured, needsReconnect, toast]);

  const pushOne = useCallback(
    async (id: number) => {
      if (kind === "invoice") {
        await pushInvoice.mutateAsync(id);
      } else {
        await pushEstimate.mutateAsync(id);
      }
    },
    [kind, pushInvoice, pushEstimate],
  );

  const pushOneWithToast = useCallback(
    async (id: number) => {
      if (!guard()) return;
      try {
        await pushOne(id);
        toast({
          title: "Pushed to Xero",
          description: kind === "invoice" ? "Invoice synced." : "Estimate synced.",
        });
      } catch (err) {
        toast({
          title: "Xero push failed",
          description: extractError(err),
          variant: "destructive",
        });
      }
    },
    [guard, pushOne, toast, kind],
  );

  const pushMany = useCallback(
    async (ids: number[], options?: PushManyOptions): Promise<BulkPushSummary | null> => {
      if (!guard()) return null;
      const summary: BulkPushSummary = {
        total: ids.length,
        succeeded: 0,
        failed: 0,
        failures: [],
        cancelled: false,
        processed: 0,
      };
      options?.onProgress?.({
        current: 0,
        total: ids.length,
        succeeded: 0,
        failed: 0,
      });
      for (let i = 0; i < ids.length; i++) {
        if (options?.signal?.aborted) {
          summary.cancelled = true;
          break;
        }
        const id = ids[i];
        options?.onProgress?.({
          current: i + 1,
          total: ids.length,
          succeeded: summary.succeeded,
          failed: summary.failed,
        });
        try {
          await pushOne(id);
          summary.succeeded += 1;
        } catch (err) {
          summary.failed += 1;
          summary.failures.push({ id, error: extractError(err) });
        }
        summary.processed += 1;
        options?.onProgress?.({
          current: i + 1,
          total: ids.length,
          succeeded: summary.succeeded,
          failed: summary.failed,
        });
      }
      const noun = `${docLabel}${summary.total !== 1 ? "s" : ""}`;
      if (summary.cancelled) {
        toast({
          title: `Canceled — pushed ${summary.succeeded} of ${summary.total} ${noun}`,
          description:
            summary.failed > 0
              ? `${summary.failed} failed before canceling.`
              : `Stopped after ${summary.processed} of ${summary.total}.`,
          variant: summary.failed > 0 ? "destructive" : undefined,
        });
      } else if (summary.failed === 0) {
        toast({ title: `Pushed ${summary.succeeded} ${noun} to Xero` });
      } else if (summary.succeeded === 0) {
        toast({
          title: `Failed to push ${summary.failed} ${noun} to Xero`,
          description: summary.failures
            .slice(0, 3)
            .map(f => `#${f.id}: ${f.error}`)
            .join("\n"),
          variant: "destructive",
        });
      } else {
        toast({
          title: `Pushed ${summary.succeeded} of ${summary.total} ${noun}`,
          description: `${summary.failed} failed. ${summary.failures
            .slice(0, 3)
            .map(f => `#${f.id}: ${f.error}`)
            .join("; ")}${summary.failures.length > 3 ? "…" : ""}`,
          variant: "destructive",
        });
      }
      return summary;
    },
    [guard, pushOne, toast, docLabel],
  );

  const isPending = kind === "invoice" ? pushInvoice.isPending : pushEstimate.isPending;

  return {
    status,
    connected,
    configured,
    needsReconnect,
    available,
    isPending,
    pushOneWithToast,
    pushMany,
  };
}
